import { Action } from 'redux';

import { Data } from '../form/form.model';

import {
    Args,
    HierarchyInfo,
    ManagerReportDataItem,
    Metadata,
    Payload,
    PayloadExtended,
    ReviewDateRange,
} from './tdcf.model';

export enum ActionType {
    TDCF_FORM__RETRIEVE_INIT_DATA = 'TDCF_FORM__RETRIEVE_INIT_DATA',
    TDCF_FORM__RETRIEVE_INIT_DATA_RESULT = 'TDCF_FORM__RETRIEVE_INIT_DATA_RESULT',
    TDCF_FORM__FORM_VALUE_CHANGE = 'TDCF_FORM__FORM_VALUE_CHANGE',
    TDCF_FORM__FORM_SAVE = 'TDCF_FORM__FORM_SAVE',
    TDCF_FORM__FORM_SAVE_RESULT = 'TDCF_FORM__FORM_SAVE_RESULT',
    TDCF_FORM__FORM_SET_READONLY = 'TDCF_FORM__FORM_SET_READONLY',
    TDCF_FORM__FORM_RESET_STATE = 'TDCF_FORM__FORM_RESET_STATE',
    TDCF_FORM__PRINT_RETRIEVE_INIT_DATA = 'TDCF_FORM__PRINT_RETRIEVE_INIT_DATA',
    TDCF_FORM__PRINT_RETRIEVE_INIT_DATA_RESULT = 'TDCF_FORM__PRINT_RETRIEVE_INIT_DATA_RESULT',
    TDCF_FORM__REPORT_RETRIEVE_INIT_DATA = 'TDCF_FORM__REPORT_RETRIEVE_INIT_DATA',
    TDCF_FORM__REPORT_RETRIEVE_INIT_DATA_RESULT = 'TDCF_FORM__REPORT_RETRIEVE_INIT_DATA_RESULT',
    TDCF_FORM__REPORT_FORM_VALUE_CHANGE = 'TDCF_FORM__REPORT_FORM_VALUE_CHANGE',
    TDCF_FORM__REPORT_GENERATE = 'TDCF_FORM__REPORT_GENERATE',
    TDCF_FORM__REPORT_GENERATE_RESULT = 'TDCF_FORM__REPORT_GENERATE_RESULT',
}

export interface RetrieveInitDataAction extends Action<ActionType> {
    templateId: number;
    userId: number;
}

export const retrieveInitData = (
    templateId: number,
    userId: number,
): RetrieveInitDataAction => ({
    type: ActionType.TDCF_FORM__RETRIEVE_INIT_DATA,
    templateId,
    userId,
});

export interface RetrieveInitDataResultAction extends RetrieveInitDataAction {
    metadata: Metadata;
    me: Array<Data<PayloadExtended>>;
    team: Array<Data<PayloadExtended>>;
    info: HierarchyInfo;
    hierarchy: HierarchyInfo;
}

export const retrieveInitDataResult = (
    templateId: number,
    userId: number,
    metadata: Metadata,
    me: Array<Data<PayloadExtended>>,
    team: Array<Data<PayloadExtended>>,
    info: HierarchyInfo,
    hierarchy: HierarchyInfo,
): RetrieveInitDataResultAction => ({
    type: ActionType.TDCF_FORM__RETRIEVE_INIT_DATA_RESULT,
    templateId,
    userId,
    metadata,
    me,
    team,
    info,
    hierarchy,
});

/// //////////////////////

export interface FormValueChangeAction extends Action<ActionType> {
    key: string;
    value: any;
}

export const formValueChange = (
    key: string,
    value: any,
): FormValueChangeAction => ({
    type: ActionType.TDCF_FORM__FORM_VALUE_CHANGE,
    key,
    value,
});

/// //////////////////////

export interface FormSaveAction extends Action<ActionType> {
    key: string;
    data: Data<Payload>;
}

export const formSave = (
    key: string,
    data: Data<Payload, Args>,
): FormSaveAction => ({
    type: ActionType.TDCF_FORM__FORM_SAVE,
    key,
    data,
});

export interface FormSaveResultAction extends FormSaveAction {
    location: string;
    payload: PayloadExtended;
}

export const formSaveResult = (
    key: string,
    data: Data<Payload>,
    location: string,
    payload?: PayloadExtended,
): FormSaveResultAction => ({
    type: ActionType.TDCF_FORM__FORM_SAVE_RESULT,
    key,
    data,
    location,
    payload,
});

/// //////////////////////

export interface FormSetReadonlyAction extends Action<ActionType> {
    key: string;
    readonly: boolean;
}

export const formSetReadonly = (
    key: string,
    readonly: boolean,
): FormSetReadonlyAction => ({
    type: ActionType.TDCF_FORM__FORM_SET_READONLY,
    key,
    readonly,
});

/// //////////////////////

export interface FormResetStateAction extends Action<ActionType> {
    key: string;
}

export const formResetState = (key: string): FormResetStateAction => ({
    type: ActionType.TDCF_FORM__FORM_RESET_STATE,
    key,
});

/// //////////////////////

export interface PrintRetrieveInitDataAction extends Action<ActionType> {
    templateId: number;
    dataId: number;
}

export const printRetrieveInitData = (
    templateId: number,
    dataId: number,
): PrintRetrieveInitDataAction => ({
    type: ActionType.TDCF_FORM__PRINT_RETRIEVE_INIT_DATA,
    templateId,
    dataId,
});

export interface PrintRetrieveInitDataResultAction
    extends PrintRetrieveInitDataAction {
    metadata: Metadata;
    payload: PayloadExtended;
}

export const printRetrieveInitDataResult = (
    templateId: number,
    dataId: number,
    metadata: Metadata,
    payload: PayloadExtended,
): PrintRetrieveInitDataResultAction => ({
    type: ActionType.TDCF_FORM__PRINT_RETRIEVE_INIT_DATA_RESULT,
    templateId,
    dataId,
    metadata,
    payload,
});

/// //////////////////////

export interface ReportRetrieveInitDataAction extends Action<ActionType> {
    templateId: number;
}

export const reportRetrieveInitData = (
    templateId: number,
): ReportRetrieveInitDataAction => ({
    type: ActionType.TDCF_FORM__REPORT_RETRIEVE_INIT_DATA,
    templateId,
});

export interface ReportRetrieveInitDataResultAction
    extends ReportRetrieveInitDataAction {
    metadata: Metadata;
}

export const reportRetrieveInitResultData = (
    templateId: number,
    metadata: Metadata,
): ReportRetrieveInitDataResultAction => ({
    type: ActionType.TDCF_FORM__REPORT_RETRIEVE_INIT_DATA_RESULT,
    templateId,
    metadata,
});

/// //////////////////////

export interface ReportFormValueChangeAction extends Action<ActionType> {
    value: any;
}

export const reportFormValueChange = (
    value: any,
): ReportFormValueChangeAction => ({
    type: ActionType.TDCF_FORM__REPORT_FORM_VALUE_CHANGE,
    value,
});

/// //////////////////////

export interface ReportGenerateAction extends Action<ActionType> {
    templateId: number;
    userId: number;
    reviewDateRanges: ReviewDateRange[];
    directTeamOnly: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const reportGenerate = (
    templateId: number,
    userId: number,
    reviewDateRanges: ReviewDateRange[],
    directTeamOnly: boolean,
) => ({
    type: ActionType.TDCF_FORM__REPORT_GENERATE,
    templateId,
    userId,
    reviewDateRanges,
    directTeamOnly,
});

export interface ReportGenerateResultAction extends ReportGenerateAction {
    data: ManagerReportDataItem[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const reportGenerateResult = (
    templateId: number,
    userId: number,
    reviewDateRanges: ReviewDateRange[],
    directTeamOnly: boolean,
    data: ManagerReportDataItem[],
) => ({
    type: ActionType.TDCF_FORM__REPORT_GENERATE_RESULT,
    templateId,
    userId,
    reviewDateRanges,
    directTeamOnly,
    data,
});
